import { useState, useCallback, useMemo } from 'react';
import { X, Camera, Upload, Video, Check, ChevronRight, Loader2, AlertCircle, Eraser, Merge, Unlink, Link2, Wand2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImageUploader } from '../common/ImageUploader';
import { SegmentEraser } from './SegmentEraser';
import { ExistingGarmentThumb } from './ExistingGarmentThumb';
import { useWardrobeStore } from '../../stores/wardrobeStore';
import { useGarmentImage } from '@/hooks/useGarmentImage';
import { garmentService } from '../../services/garmentService';
import { compressImages } from '../../utils/imageCompression';
import { getQualityScore, isLowQuality } from '@/services/garmentQuality';
import toast from 'react-hot-toast';
import type { Garment } from '@/types';

interface Props { onClose: () => void; }

type Step = 'upload' | 'processing' | 'confirm' | 'saved';

interface DetectedGarment {
  id: string;
  segmentImageUrl: string;
  segmentImageBase64: string;
  /** Generated product photo (base64 PNG). */
  productImageBase64: string;
  productImageUrl: string;
  category: string;
  subCategory: string;
  pattern: string;
  material: string;
  primaryColors: Array<{ hex: string; name: string; percentage: number }>;
  confidence: number;
  selected: boolean;
  size: string;
  brand: string;
  qualityScore?: number;
}

const SEGMENT_COLORS = ['#6C3CE1', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#EC4899'];

/** Euclidean distance between two hex colors in RGB space */
function colorDistance(hex1: string, hex2: string): number {
  const parse = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const [r1, g1, b1] = parse(hex1);
  const [r2, g2, b2] = parse(hex2);
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

const CATEGORY_OPTIONS = ['top', 'bottom', 'dress', 'outerwear', 'shoes', 'accessory', 'other'];
const SUB_CATEGORY_OPTIONS = ['upper-clothes', 't-shirt', 'shirt', 'blouse', 'sweater', 'hoodie', 'jacket', 'coat', 'vest', 'tank-top', 'pants', 'jeans', 'trousers', 'shorts', 'skirt', 'dress', 'shoe', 'hat', 'bag', 'scarf', 'belt', 'sunglasses'];
const PATTERN_OPTIONS = ['solid', 'striped', 'plaid', 'floral', 'polka_dot', 'geometric', 'abstract', 'animal_print'];
const MATERIAL_OPTIONS = ['cotton', 'denim', 'silk', 'wool', 'polyester', 'leather', 'linen', 'cashmere', 'nylon', 'velvet', 'chiffon', 'satin', 'knit'];

export function AddGarmentModal({ onClose }: Props) {
  const [step, setStep] = useState<Step>('upload');
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState('');
  const [detectedGarments, setDetectedGarments] = useState<DetectedGarment[]>([]);
  const [activeGarmentIndex, setActiveGarmentIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [eraserIndex, setEraserIndex] = useState<number | null>(null);
  // Merge state: maps garment index → group ID. Garments with same group ID are merged.
  const [mergeMap, setMergeMap] = useState<Record<number, string>>({});
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState<Set<number>>(new Set());
  // Link to existing garment state
  const [linkingIndex, setLinkingIndex] = useState<number | null>(null);
  const [linkedExisting, setLinkedExisting] = useState<Record<number, string>>({}); // garment index → existing garment ID
  // Auto-suggest matches against closet
  const [autoSuggestions, setAutoSuggestions] = useState<Record<number, string[]>>({}); // garment index → [existing garment IDs]
  const [showSuggestion, setShowSuggestion] = useState<{ index: number; existingId: string } | null>(null);
  const { addGarment, garments: closetGarments, updateGarment: updateExistingGarment } = useWardrobeStore();

  // Convert a File to base64 JPEG via canvas (handles HEIC, WebP, etc.)
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas not supported')); return; }
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          resolve(dataUrl.split(',')[1]);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = reader.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;
    setStep('processing');
    setProgress(5);
    setProgressMessage('Compressing images…');
    setError('');

    try {
      const compressed = await compressImages(files, 1024, 0.85);
      const filesToProcess = compressed.length > 0 ? compressed : files;

      // Render (B2 on CPU) is primary; Modal is fallback
      const RENDER_URL = 'https://vestir-api.onrender.com/api';
      const MODAL_URL = 'https://almogtavor--vestir-garment-segmentation-web.modal.run';
      const SEGMENT_URL = import.meta.env.VITE_SEGMENTATION_API_URL || RENDER_URL;

      const categoryMap: Record<string, string> = {
        'tops': 'top', 'bottoms': 'bottom', 'dresses': 'dress',
        'outerwear': 'outerwear', 'shoes': 'shoes', 'accessories': 'accessory',
      };

      const allGarments: DetectedGarment[] = [];
      let garmentCounter = 0;
      const totalStart = Date.now();

      for (let fileIdx = 0; fileIdx < filesToProcess.length; fileIdx++) {
        const file = filesToProcess[fileIdx];
        const fileProgress = ((fileIdx) / filesToProcess.length) * 80 + 10;
        const imageStart = Date.now();
        const totalElapsed = Math.round((Date.now() - totalStart) / 1000);

        setProgress(Math.round(fileProgress));
        setProgressMessage(`Segmenting image ${fileIdx + 1} of ${filesToProcess.length}…\n${totalElapsed}s total`);

        const base64String = await fileToBase64(file);

        const payload = JSON.stringify({ image_b64: base64String });
        const fetchOpts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload };

        // Try primary URL, fall back to Modal if it fails
        let response: Response;
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 15000);
          response = await fetch(`${SEGMENT_URL}/segment`, { ...fetchOpts, signal: controller.signal });
          clearTimeout(timeout);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        } catch {
          // Fallback to Modal
          response = await fetch(`${MODAL_URL}/segment`, fetchOpts);
        }

        if (!response.ok) {
          console.error(`Segmentation failed for image ${fileIdx + 1}: ${response.status}`);
          continue;
        }

        const result = await response.json();
        const imageSec = Math.round((Date.now() - imageStart) / 1000);
        const totalSec = Math.round((Date.now() - totalStart) / 1000);
        setProgressMessage(`Segmenting image ${fileIdx + 1} of ${filesToProcess.length}…\n${imageSec}s this image · ${totalSec}s total`);

        if (result.garments && result.garments.length > 0) {
          for (const g of result.garments) {
            allGarments.push({
              id: `garment-${garmentCounter++}`,
              segmentImageBase64: g.segment_image_b64 || '',
              segmentImageUrl: g.segment_image_b64
                ? `data:image/png;base64,${g.segment_image_b64}`
                : '',
              productImageBase64: '',
              productImageUrl: '',
              category: categoryMap[g.category] || g.category || 'top',
              subCategory: g.sub_category || 'upper-clothes',
              pattern: g.pattern || 'solid',
              material: g.material || 'cotton',
              primaryColors: (g.primary_colors || []).map((c: any) => ({
                hex: c.hex,
                name: c.hex,
                percentage: c.percentage,
              })),
              confidence: g.confidence ?? 0.85,
              selected: true,
              size: '',
              brand: '',
            });
          }
        }
      }

      if (allGarments.length === 0) {
        throw new Error('No garments detected in any of the uploaded images');
      }

      // Score image quality for each detected garment
      setProgressMessage('Checking image quality…');
      const lowQualityWarnings: string[] = [];
      for (const g of allGarments) {
        if (g.segmentImageUrl) {
          g.qualityScore = await getQualityScore(g.segmentImageUrl);
          if (isLowQuality(g.qualityScore)) {
            lowQualityWarnings.push(g.subCategory || g.category);
          }
        }
      }
      if (lowQualityWarnings.length > 0) {
        toast(`Low quality image${lowQualityWarnings.length > 1 ? 's' : ''} detected for: ${lowQualityWarnings.join(', ')}. Consider retaking.`, { icon: '⚠️', duration: 5000 });
      }

      setDetectedGarments(allGarments);
      setMergeMap(autoDetectMerges(allGarments));
      // Auto-suggest matches against existing closet (2B)
      const suggestions = findClosetMatches(allGarments);
      setAutoSuggestions(suggestions);
      // Show first suggestion if any
      const firstKey = Object.keys(suggestions)[0];
      if (firstKey !== undefined) {
        const idx = Number(firstKey);
        setShowSuggestion({ index: idx, existingId: suggestions[idx][0] });
      }
      setActiveGarmentIndex(0);
      setProgress(100);
      setProgressMessage(`Found ${allGarments.length} garment${allGarments.length > 1 ? 's' : ''} across ${filesToProcess.length} image${filesToProcess.length > 1 ? 's' : ''}!`);
      setTimeout(() => setStep('confirm'), 300);
    } catch (err: any) {
      console.error('Upload/segmentation error:', err);
      setError(err.message || 'Failed to process images');
      setStep('upload');
    }
  }, [files, fileToBase64]);

  const updateGarment = (index: number, updates: Partial<DetectedGarment>) => {
    setDetectedGarments(prev => prev.map((g, i) => i === index ? { ...g, ...updates } : g));
  };

  const toggleGarmentSelected = (index: number) => {
    setDetectedGarments(prev => prev.map((g, i) => i === index ? { ...g, selected: !g.selected } : g));
  };

  // Auto-detect merge groups when garments change (same category + similar dominant color)
  const autoDetectMerges = useCallback((garments: DetectedGarment[]) => {
    const newMap: Record<number, string> = {};
    const used = new Set<number>();

    for (let i = 0; i < garments.length; i++) {
      if (used.has(i)) continue;
      const group: number[] = [i];
      const gi = garments[i];

      for (let j = i + 1; j < garments.length; j++) {
        if (used.has(j)) continue;
        const gj = garments[j];
        // Same category and sub-category = likely same garment from different photos
        if (gi.category === gj.category && gi.subCategory === gj.subCategory) {
          // Check if dominant colors are similar
          const ci = gi.primaryColors[0]?.hex || '';
          const cj = gj.primaryColors[0]?.hex || '';
          if (ci && cj && colorDistance(ci, cj) < 60) {
            group.push(j);
          }
        }
      }

      if (group.length > 1) {
        const groupId = `auto-${i}`;
        for (const idx of group) {
          newMap[idx] = groupId;
          used.add(idx);
        }
      }
    }
    return newMap;
  }, []);

  // Auto-suggest matches against existing closet garments (2B)
  const findClosetMatches = useCallback((garments: DetectedGarment[]) => {
    const suggestions: Record<number, string[]> = {};
    for (let i = 0; i < garments.length; i++) {
      const detected = garments[i];
      const matches: string[] = [];
      for (const existing of closetGarments) {
        // Same category
        if (existing.category !== detected.category) continue;
        // Similar dominant color
        const detectedColor = detected.primaryColors[0]?.hex;
        const existingColor = typeof existing.colors?.[0] === 'string' ? existing.colors[0] : '';
        if (detectedColor && existingColor && colorDistance(detectedColor, existingColor) < 80) {
          matches.push(existing.id);
        }
      }
      if (matches.length > 0) {
        suggestions[i] = matches;
      }
    }
    return suggestions;
  }, [closetGarments]);

  // Link a detected garment to an existing closet garment
  const linkToExisting = useCallback((detectedIndex: number, existingId: string) => {
    setLinkedExisting(prev => ({ ...prev, [detectedIndex]: existingId }));
    // Deselect since it'll merge into existing instead of creating new
    setDetectedGarments(prev => prev.map((g, i) => i === detectedIndex ? { ...g, selected: false } : g));
    setLinkingIndex(null);
    setShowSuggestion(null);
  }, []);

  // Unlink a detected garment from existing
  const unlinkFromExisting = useCallback((detectedIndex: number) => {
    setLinkedExisting(prev => {
      const next = { ...prev };
      delete next[detectedIndex];
      return next;
    });
    setDetectedGarments(prev => prev.map((g, i) => i === detectedIndex ? { ...g, selected: true } : g));
  }, []);

  // Compute merge groups: { groupId → [indices] }
  const mergeGroups = useMemo(() => {
    const groups: Record<string, number[]> = {};
    for (const [idxStr, groupId] of Object.entries(mergeMap)) {
      const idx = Number(idxStr);
      if (!groups[groupId]) groups[groupId] = [];
      groups[groupId].push(idx);
    }
    return groups;
  }, [mergeMap]);

  // Unmerge a group
  const unmergeGroup = useCallback((groupId: string) => {
    setMergeMap(prev => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(next)) {
        if (v === groupId) delete next[Number(k)];
      }
      return next;
    });
  }, []);

  // Execute merge on current selection
  const executeMerge = useCallback(() => {
    if (mergeSelection.size < 2) return;
    const indices = Array.from(mergeSelection).sort((a, b) => a - b);

    // Check if any selected item is already in a group - add to that group
    let targetGroup: string | null = null;
    for (const idx of indices) {
      if (mergeMap[idx]) {
        targetGroup = mergeMap[idx];
        break;
      }
    }

    const groupId = targetGroup || `manual-${Date.now()}`;
    setMergeMap(prev => {
      const next = { ...prev };
      for (const idx of indices) {
        next[idx] = groupId;
      }
      return next;
    });
    setMergeSelection(new Set());
    setMergeMode(false);
  }, [mergeSelection, mergeMap]);

  // Toggle merge selection
  const toggleMergeSelect = useCallback((index: number) => {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const linkedCount = Object.keys(linkedExisting).length;
  const selectedCount = detectedGarments.filter(g => g.selected).length + linkedCount;
  const activeGarment = detectedGarments[activeGarmentIndex];

  // Render a single garment card (used by both grouped and ungrouped)
  const renderGarmentCard = useCallback((index: number) => {
    const garment = detectedGarments[index];
    if (!garment) return null;
    const color = SEGMENT_COLORS[index % SEGMENT_COLORS.length];
    const isActive = index === activeGarmentIndex;
    const isMergeSelected = mergeSelection.has(index);

    return (
      <motion.div
        key={garment.id}
        layout
        whileTap={{ scale: 0.95 }}
        onClick={() => mergeMode ? toggleMergeSelect(index) : setActiveGarmentIndex(index)}
        className="flex-shrink-0 cursor-pointer"
      >
        <div
          className={`relative w-20 rounded-xl overflow-hidden shadow-sm transition-all duration-200 ${
            mergeMode && isMergeSelected ? 'ring-2 ring-[#6C3CE1] shadow-md' :
            isActive && !mergeMode ? 'shadow-md ring-2' : 'ring-1 ring-gray-200'
          }`}
          style={{
            ...(isActive && !mergeMode ? { '--tw-ring-color': color } as any : {}),
          }}
        >
          <div
            className="w-20 h-20 relative"
            style={{
              backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
              backgroundSize: '10px 10px',
              backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
              backgroundColor: '#fafafa',
            }}
          >
            {(garment.productImageUrl || garment.segmentImageUrl) ? (
              <>
                <img
                  src={garment.productImageUrl || garment.segmentImageUrl}
                  alt={garment.category}
                  className="w-full h-full object-contain"
                />
                {!mergeMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEraserIndex(index); }}
                    className="absolute top-1 right-1 p-0.5 bg-black/50 rounded text-white hover:bg-black/70 transition-colors"
                    title="Edit mask"
                  >
                    <Eraser size={10} />
                  </button>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-8 h-8 rounded-lg opacity-20" style={{ backgroundColor: color }} />
              </div>
            )}
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: color }} />
            {/* Merge mode checkbox */}
            {mergeMode && (
              <div className={`absolute top-1 left-1 w-4 h-4 rounded-full flex items-center justify-center ${
                isMergeSelected ? 'bg-[#6C3CE1] text-white' : 'bg-white/80 border border-gray-300'
              }`}>
                {isMergeSelected && <Check size={10} strokeWidth={3} />}
              </div>
            )}
          </div>
          <div className="px-1.5 py-1 bg-white">
            <div className="flex items-center gap-1">
              {!mergeMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleGarmentSelected(index); }}
                  className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                    garment.selected ? 'border-transparent text-white' : 'border-gray-300 bg-white'
                  }`}
                  style={garment.selected ? { backgroundColor: color } : {}}
                >
                  {garment.selected && <Check size={8} strokeWidth={3} />}
                </button>
              )}
              <span className="text-[10px] font-medium text-gray-700 truncate capitalize">
                {garment.category}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }, [detectedGarments, activeGarmentIndex, mergeMode, mergeSelection, toggleMergeSelect, toggleGarmentSelected]);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      // First, handle garments linked to existing closet items (2A/2B merges)
      for (const [idxStr, existingId] of Object.entries(linkedExisting)) {
        const idx = Number(idxStr);
        const detected = detectedGarments[idx];
        if (!detected) continue;
        const existing = closetGarments.find(g => g.id === existingId);
        if (!existing) continue;

        const newImageUrl = detected.segmentImageUrl
          || (detected.segmentImageBase64 ? `data:image/png;base64,${detected.segmentImageBase64}` : '');
        if (newImageUrl) {
          const currentUrls = existing.imageUrls || [];
          await updateExistingGarment(existingId, { imageUrls: [...currentUrls, newImageUrl] });
        }
      }

      // Build save list: merge grouped garments into single entries (skip linked ones)
      const processed = new Set<number>(Object.keys(linkedExisting).map(Number));
      const toSave: { primary: DetectedGarment; imageUrls: string[] }[] = [];

      detectedGarments.forEach((garment, index) => {
        if (processed.has(index) || !garment.selected) return;

        const groupId = mergeMap[index];
        if (groupId) {
          // This garment is in a merge group - collect all images from the group
          const groupIndices = Object.entries(mergeMap)
            .filter(([, gid]) => gid === groupId)
            .map(([idx]) => Number(idx))
            .filter(idx => detectedGarments[idx]?.selected);

          // Sort by quality score (highest first) so best image is representative
          const sortedIndices = [...groupIndices].sort((a, b) => {
            const qa = detectedGarments[a]?.qualityScore ?? 0;
            const qb = detectedGarments[b]?.qualityScore ?? 0;
            return qb - qa;
          });

          const urls: string[] = [];
          for (const gi of sortedIndices) {
            const g = detectedGarments[gi];
            const url = g.segmentImageUrl || (g.segmentImageBase64 ? `data:image/png;base64,${g.segmentImageBase64}` : '');
            if (url) urls.push(url);
            processed.add(gi);
          }
          const bestPrimary = detectedGarments[sortedIndices[0]] || garment;
          toSave.push({ primary: bestPrimary, imageUrls: urls });
        } else {
          const url = garment.segmentImageUrl || (garment.segmentImageBase64 ? `data:image/png;base64,${garment.segmentImageBase64}` : '');
          toSave.push({ primary: garment, imageUrls: url ? [url] : [] });
          processed.add(index);
        }
      });

      for (const { primary: garment, imageUrls } of toSave) {
        const fallbackUrls = imageUrls.length > 0 ? imageUrls : (files.length > 0 ? [URL.createObjectURL(files[0])] : []);

        const savedGarment = {
          id: `garment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          userId: 'local',
          name: `${garment.subCategory || garment.category}`,
          category: garment.category as any,
          subCategory: garment.subCategory,
          colors: garment.primaryColors.map(c => ({
            hex: c.hex,
            name: c.name || c.hex,
            percentage: c.percentage,
          })),
          pattern: garment.pattern as any,
          material: garment.material as any,
          seasons: [] as any[],
          occasions: [] as any[],
          brand: garment.brand || '',
          size: garment.size || '',
          imageUrls: fallbackUrls,
          thumbnailUrl: fallbackUrls[0] || '',
          meshUrl: null,
          purchaseLinks: [],
          timesWorn: 0,
          lastWorn: null,
          isFavorite: false,
          notes: '',
          tags: [],
          costPerWear: null,
          purchasePrice: null,
          purchaseDate: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        addGarment(savedGarment as any);
      }

      setStep('saved');
      setTimeout(onClose, 1000);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save garment';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div initial={{ y: 100 }} animate={{ y: 0 }}
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-hidden sm:mb-0 mb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold">
            {step === 'upload' && 'Add to Closet'}
            {step === 'processing' && 'Processing…'}
            {step === 'confirm' && 'Confirm Details'}
            {step === 'saved' && 'Saved!'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(85vh-140px)]">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-xl text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 'upload' && (
              <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <ImageUploader images={files} onImagesChange={setFiles} maxFiles={10} accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.webp'], 'video/*': ['.mp4', '.mov'] }} />
                <button onClick={handleUpload} disabled={files.length === 0}
                  className="w-full mt-4 py-3 rounded-xl bg-[#6C3CE1] text-white font-medium disabled:opacity-40 flex items-center justify-center gap-2">
                  Add to Closet <ChevronRight size={18} />
                </button>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.capture = 'environment';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) setFiles(prev => [...prev, file]);
                      };
                      input.click();
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-100 text-sm font-medium hover:bg-gray-200">
                    <Camera size={18} /> Camera
                  </button>
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'video/*';
                      input.capture = 'environment';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) setFiles(prev => [...prev, file]);
                      };
                      input.click();
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-100 text-sm font-medium hover:bg-gray-200">
                    <Video size={18} /> Video Scan
                  </button>
                </div>
              </motion.div>
            )}

            {step === 'processing' && (
              <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                <Loader2 size={40} className="mx-auto text-[#6C3CE1] animate-spin mb-4" />
                <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                  <motion.div animate={{ width: `${progress}%` }} className="bg-[#6C3CE1] h-2 rounded-full" />
                </div>
                <p className="text-sm text-gray-600 whitespace-pre-line">{progressMessage}</p>
                <p className="text-xs text-gray-400 mt-1">{progress}%</p>
              </motion.div>
            )}

            {step === 'confirm' && (
              <motion.div key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                {/* Garment count header */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    Found {detectedGarments.length} garment{detectedGarments.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    {mergeMode ? (
                      <>
                        <button
                          onClick={executeMerge}
                          disabled={mergeSelection.size < 2}
                          className="text-xs font-medium text-white bg-[#6C3CE1] px-3 py-1 rounded-lg disabled:opacity-40"
                        >
                          Merge ({mergeSelection.size})
                        </button>
                        <button
                          onClick={() => { setMergeMode(false); setMergeSelection(new Set()); }}
                          className="text-xs text-gray-500"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setMergeMode(true)}
                        className="text-xs font-medium text-[#6C3CE1] flex items-center gap-1"
                      >
                        <Merge size={12} /> Merge
                      </button>
                    )}
                  </div>
                </div>

                {/* Horizontal scroll of segment cards with merge groups */}
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                  {(() => {
                    // Build render order: grouped items together, ungrouped individually
                    const rendered = new Set<number>();
                    const elements: React.ReactNode[] = [];

                    // First render merge groups
                    for (const [groupId, indices] of Object.entries(mergeGroups)) {
                      elements.push(
                        <div key={`group-${groupId}`} className="flex-shrink-0">
                          <div className="rounded-xl border-2 border-dashed border-[#6C3CE1]/40 bg-[#6C3CE1]/5 p-1.5 flex gap-1.5">
                            {indices.map(index => {
                              rendered.add(index);
                              return renderGarmentCard(index);
                            })}
                          </div>
                          <button
                            onClick={() => unmergeGroup(groupId)}
                            className="text-[10px] text-gray-400 hover:text-red-500 mt-1 float-right flex items-center gap-0.5"
                          >
                            <Unlink size={10} /> unmerge
                          </button>
                        </div>
                      );
                    }

                    // Then render ungrouped items
                    detectedGarments.forEach((_, index) => {
                      if (!rendered.has(index)) {
                        elements.push(
                          <div key={`single-${index}`} className="flex-shrink-0">
                            {renderGarmentCard(index)}
                          </div>
                        );
                      }
                    });

                    return elements;
                  })()}
                </div>

                {/* Auto-suggestion banner (2B) */}
                <AnimatePresence>
                  {showSuggestion && (() => {
                    const detected = detectedGarments[showSuggestion.index];
                    const existing = closetGarments.find(g => g.id === showSuggestion.existingId);
                    if (!detected || !existing) return null;
                    return (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-amber-50 border border-amber-200 rounded-xl p-3"
                      >
                        <div className="flex items-center gap-1.5 mb-2">
                          <Wand2 size={14} className="text-amber-600" />
                          <p className="text-xs font-medium text-amber-800">Already in your closet?</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-center">
                            <div className="w-14 h-14 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                              <img src={detected.segmentImageUrl} alt="" className="w-full h-full object-contain" />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-0.5">New</p>
                          </div>
                          <span className="text-gray-400 text-lg">→</span>
                          <ExistingGarmentThumb garment={existing} />
                          <div className="flex-1 text-right">
                            <p className="text-[10px] text-gray-500 capitalize">{existing.name || existing.category}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => linkToExisting(showSuggestion.index, showSuggestion.existingId)}
                            className="flex-1 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium"
                          >
                            Yes, merge
                          </button>
                          <button
                            onClick={() => {
                              // Skip to next suggestion
                              const keys = Object.keys(autoSuggestions).map(Number);
                              const curIdx = keys.indexOf(showSuggestion.index);
                              if (curIdx + 1 < keys.length) {
                                const nextKey = keys[curIdx + 1];
                                setShowSuggestion({ index: nextKey, existingId: autoSuggestions[nextKey][0] });
                              } else {
                                setShowSuggestion(null);
                              }
                            }}
                            className="flex-1 py-1.5 rounded-lg bg-white border border-gray-200 text-xs text-gray-600"
                          >
                            No, keep separate
                          </button>
                        </div>
                      </motion.div>
                    );
                  })()}
                </AnimatePresence>

                {/* Linked to existing indicator */}
                {linkedExisting[activeGarmentIndex] && (() => {
                  const existing = closetGarments.find(g => g.id === linkedExisting[activeGarmentIndex]);
                  if (!existing) return null;
                  return (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                      <Link2 size={14} className="text-green-600" />
                      <p className="text-xs text-green-800 flex-1">
                        Will merge into <span className="font-medium capitalize">{existing.name || existing.category}</span>
                      </p>
                      <button onClick={() => unlinkFromExisting(activeGarmentIndex)} className="text-xs text-red-500">
                        Unlink
                      </button>
                    </div>
                  );
                })()}

                {/* Active garment divider */}
                {activeGarment && (
                  <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-medium text-gray-500 capitalize flex items-center gap-1.5">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: SEGMENT_COLORS[activeGarmentIndex % SEGMENT_COLORS.length] }}
                      />
                      {activeGarment.category}
                      {/* Link to existing button (2A) */}
                      {!linkedExisting[activeGarmentIndex] && (
                        <button
                          onClick={() => setLinkingIndex(activeGarmentIndex)}
                          className="ml-1 text-[10px] text-[#6C3CE1] font-medium flex items-center gap-0.5"
                        >
                          <Link2 size={10} /> Link to existing
                        </button>
                      )}
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                )}

                {/* Editable attributes for the active garment */}
                {activeGarment && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeGarment.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-3"
                    >
                      <p className="text-xs text-gray-400">AI-detected attributes (tap to edit)</p>

                      <div>
                        <label className="text-xs text-gray-500">Category</label>
                        <select
                          value={activeGarment.category}
                          onChange={e => updateGarment(activeGarmentIndex, { category: e.target.value })}
                          className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize"
                        >
                          {CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">Sub-category</label>
                        <select
                          value={activeGarment.subCategory}
                          onChange={e => updateGarment(activeGarmentIndex, { subCategory: e.target.value })}
                          className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize"
                        >
                          {SUB_CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">Pattern</label>
                        <select
                          value={activeGarment.pattern}
                          onChange={e => updateGarment(activeGarmentIndex, { pattern: e.target.value })}
                          className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize"
                        >
                          {PATTERN_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">Material</label>
                        <select
                          value={activeGarment.material}
                          onChange={e => updateGarment(activeGarmentIndex, { material: e.target.value })}
                          className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize"
                        >
                          {MATERIAL_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">Size</label>
                          <input
                            value={activeGarment.size}
                            onChange={e => updateGarment(activeGarmentIndex, { size: e.target.value })}
                            placeholder="M, L, 10..."
                            className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Brand</label>
                          <input
                            value={activeGarment.brand}
                            onChange={e => updateGarment(activeGarmentIndex, { brand: e.target.value })}
                            placeholder="Optional"
                            className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-gray-500">Colors</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {activeGarment.primaryColors.map((c, i) => (
                            <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-lg">
                              <div
                                className="w-4 h-4 rounded-full border border-gray-200"
                                style={{ backgroundColor: c.hex }}
                              />
                              <span className="text-xs text-gray-700">{c.name}</span>
                              {c.percentage > 0 && (
                                <span className="text-[10px] text-gray-400">{c.percentage}%</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}
              </motion.div>
            )}

            {step === 'saved' && (
              <motion.div key="saved" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-12">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check size={28} className="text-green-600" />
                </div>
                <p className="font-medium">Added to your closet!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {step === 'confirm' && (
          <div className="px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t bg-white">
            <button
              onClick={handleSave}
              disabled={selectedCount === 0 || saving}
              className="w-full py-3 rounded-xl bg-[#6C3CE1] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  {linkedCount > 0 && detectedGarments.filter(g => g.selected).length === 0
                    ? `Merge ${linkedCount} to Closet`
                    : `Save ${selectedCount > 1 ? `${selectedCount} Garments` : 'to Closet'}`}
                </>
              )}
            </button>
          </div>
        )}
      </motion.div>

      {/* Link to existing - mini closet browser (2A) */}
      <AnimatePresence>
        {linkingIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
            onClick={() => setLinkingIndex(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-white rounded-t-2xl w-full max-w-lg max-h-[70vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Link to existing garment</p>
                  <p className="text-xs text-gray-400">
                    {detectedGarments[linkingIndex]?.category && `Showing ${detectedGarments[linkingIndex].category}s`}
                  </p>
                </div>
                <button onClick={() => setLinkingIndex(null)} className="p-1"><X size={18} /></button>
              </div>
              <div className="overflow-y-auto p-4 grid grid-cols-3 gap-3 max-h-[50vh]">
                {closetGarments
                  .filter(g => {
                    // Filter by same category, or show all if no match
                    const detected = detectedGarments[linkingIndex];
                    if (!detected) return true;
                    return g.category === detected.category;
                  })
                  .map(existing => (
                    <div key={existing.id} className="text-center">
                      <ExistingGarmentThumb
                        garment={existing}
                        size="md"
                        selected={linkedExisting[linkingIndex] === existing.id}
                        onClick={() => linkToExisting(linkingIndex, existing.id)}
                      />
                      <p className="text-[10px] text-gray-500 mt-1 truncate capitalize">{existing.name || existing.category}</p>
                    </div>
                  ))
                }
                {closetGarments.filter(g => g.category === detectedGarments[linkingIndex]?.category).length === 0 && (
                  <div className="col-span-3 text-center text-gray-400 py-8">
                    <p className="text-sm">No matching garments in closet</p>
                    <button onClick={() => setLinkingIndex(null)} className="text-xs text-[#6C3CE1] mt-2">Cancel</button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Segment Eraser overlay */}
      {eraserIndex !== null && detectedGarments[eraserIndex] && (
        <SegmentEraser
          imageDataUrl={detectedGarments[eraserIndex].productImageUrl || detectedGarments[eraserIndex].segmentImageUrl}
          onSave={(editedDataUrl) => {
            updateGarment(eraserIndex, {
              segmentImageUrl: editedDataUrl,
              segmentImageBase64: editedDataUrl.split(',')[1] || '',
              productImageUrl: '',
            });
            setEraserIndex(null);
          }}
          onCancel={() => setEraserIndex(null)}
        />
      )}
    </motion.div>
  );
}
